




















import {
  Component, Prop, Ref, Vue, Watch,
} from 'vue-property-decorator';
import { MerchantModel } from '@/models/MerchantModel';
import { CSSPartialDeclaration, LocationPoint } from '@/types';
import { USA_CENTER_COORDINATES } from '@/utils/geolocation-utils';
import { initializeGoogleMaps, MAP_ICONS } from '@/utils/google-maps-utils';
import { LocationStatus } from '@/enums';
import MerchantPopover from '@/components/merchant-list/MerchantPopover.vue';
import MapContainer from '@/components/common/MapContainer.vue';

const POPOVER_WIDTH = 250;
const POPOVER_HEIGHT = 200;
const POPOVER_MARGIN = 8;
const PIN_HEIGHT = 40;

@Component({
  components: {
    MapContainer,
    MerchantPopover,
  },
})
export default class LocationsMap extends Vue {
  @Prop({ required: true }) private readonly bounceId!: number;

  @Prop({ required: true }) private readonly distanceFn!: (merchant: MerchantModel) => number | null;

  @Ref('mapContainerRef') private readonly mapContainerRef!: Vue;

  private map!: google.maps.Map;

  private mapOverlay!: google.maps.OverlayView;

  private markers = new Map<number, google.maps.Marker>();

  private merchantPopover: { merchant: MerchantModel; style: CSSPartialDeclaration } | null = null;

  mounted(): void {
    this.initMap();
  }

  @Watch('location')
  private onLocationChange(location: LocationPoint | null) {
    if (location) {
      const center: google.maps.LatLng = new window.google.maps.LatLng(location.latitude, location.longitude);
      this.map.setCenter(center);
      this.map.setZoom(12);
    }
  }

  @Watch('bounceId')
  private onBounceIdChange(newId: number | null, oldId: number | null) {
    if (newId) {
      const marker = this.markers.get(newId);
      if (marker) {
        marker.setAnimation(window.google.maps.Animation.BOUNCE);
      }
    }

    if (oldId) {
      const marker = this.markers.get(oldId);
      if (marker) {
        marker.setAnimation(null);
      }
    }
  }

  @Watch('filteredMerchantIds')
  private onFilterChanged() {
    this.markers.forEach((marker, id) => {
      if (this.filteredMerchantIds.includes(id)) {
        if (!marker.getMap()) {
          marker.setMap(this.map);
        }
      } else {
        marker.setMap(null);
      }
    });
  }

  @Watch('merchants')
  private onMerchantsLoaded() {
    if (!this.markers.size && this.map && this.merchants?.length) {
      this.initMarkers();
    }
  }

  private get filteredMerchantIds(): number[] {
    return this.$store.getters.filteredMerchantList.map((e: MerchantModel) => e.id);
  }

  private get location(): LocationPoint | null {
    return this.$store.state.location;
  }

  private get merchants(): MerchantModel[] {
    return this.$store.state.merchantList;
  }

  private onPopoverClick(): void {
    if (this.$vuetify.breakpoint.smAndDown) {
      this.$router.push(`/merchant/${this.merchantPopover?.merchant.id}`);
    }
  }

  public async initMap(): Promise<void> {
    const google = await initializeGoogleMaps();
    const latitude = this.location?.latitude || USA_CENTER_COORDINATES.latitude;
    const longitude = this.location?.longitude || USA_CENTER_COORDINATES.longitude;

    const position: google.maps.LatLng = new google.maps.LatLng(latitude, longitude);

    const mapElement = this.mapContainerRef.$refs.mapRef as HTMLElement;

    this.map = new google.maps.Map(mapElement, {
      zoom: this.location ? 12 : 4,
      center: position,
      fullscreenControl: false,
    });

    if (!this.markers.size && this.merchants?.length) {
      this.initMarkers();
    }

    this.map.addListener('click', () => {
      if (this.$vuetify.breakpoint.smAndDown && this.merchantPopover) {
        this.merchantPopover = null;
      }
    });

    this.map.addListener('dragstart', () => {
      if (this.$vuetify.breakpoint.smAndDown && this.merchantPopover) {
        this.merchantPopover = null;
      }
    });

    this.mapOverlay = new google.maps.OverlayView();
    this.mapOverlay.draw = () => {
      //
    };
    this.mapOverlay.setMap(this.map);
  }

  public initMarkers(): void {
    const markers = new Map<number, google.maps.Marker>();
    const { google } = window;

    this.$store.state.merchantList.forEach((e: MerchantModel) => {
      const markerPosition: google.maps.LatLng = new google.maps.LatLng(Number(e.latitude), Number(e.longitude));

      const marker = new google.maps.Marker({
        map: this.filteredMerchantIds.includes(e.id) ? this.map : undefined,
        position: markerPosition,
        icon: e.state === LocationStatus.Open ? MAP_ICONS.mapIconPink : MAP_ICONS.mapIconBlack,
      });

      marker.addListener('mouseover', () => {
        if (this.$vuetify.breakpoint.mdAndUp) {
          const point: google.maps.Point = this.mapOverlay.getProjection().fromLatLngToContainerPixel(markerPosition);

          this.merchantPopover = {
            merchant: e,
            style: this.popoverStyle(point),
          };
        }
      });

      marker.addListener('mouseout', () => {
        if (this.$vuetify.breakpoint.mdAndUp) {
          this.merchantPopover = null;
        }
      });

      marker.addListener('click', () => {
        if (this.$vuetify.breakpoint.mdAndUp) {
          this.$router.push(`/merchant/${e.id}`);
        } else {
          const point: google.maps.Point = this.mapOverlay.getProjection().fromLatLngToContainerPixel(markerPosition);

          this.merchantPopover = {
            merchant: e,
            style: this.popoverStyle(point),
          };
        }
      });

      markers.set(e.id, marker);
    });
    this.markers = markers;
  }

  private popoverStyle(pinPoint: google.maps.Point): CSSPartialDeclaration {
    const style: CSSPartialDeclaration = {
      width: `${POPOVER_WIDTH}px`,
      position: 'absolute',
    };
    const mapWidth = this.mapContainerRef.$el.getBoundingClientRect().width;
    const mapHeight = this.mapContainerRef.$el.getBoundingClientRect().height;

    if (pinPoint.x - (POPOVER_WIDTH / 2) < POPOVER_MARGIN) {
      style.left = `${POPOVER_MARGIN}px`;
    } else if (pinPoint.x + (POPOVER_WIDTH / 2) > mapWidth - POPOVER_MARGIN) {
      style.right = `${POPOVER_MARGIN}px`;
    } else {
      style.left = `${pinPoint.x - (POPOVER_WIDTH / 2)}px`;
    }

    if (pinPoint.y + POPOVER_HEIGHT > mapHeight) {
      style.bottom = `${mapHeight - pinPoint.y + PIN_HEIGHT + POPOVER_MARGIN}px`;
    } else {
      style.top = `${pinPoint.y + POPOVER_MARGIN}px`;
    }

    return style;
  }
}
